import { useId, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import type { ErpConnectionView, ErpMatchApplyResult, ErpMatchImportPreview, ErpMatchRow } from '../../../api';
import { ApiError, erpApi } from '../../../api';
import { useIdempotencyKey } from '../../../idempotency';
import { Callout, Dialog, Field, FilterChips, Icon, Input, Pager, Select, Stepper, Table } from '../../primitives';
import { LoadingRows, StatusBadge } from '../../ui';
import type { LinkedWebsite } from '../labels';
import { formatNumber, websiteLabel } from '../labels';
import { BatchResult } from './BatchResult';
import type { LeftOutRow } from './BatchResult';
import { MAX_MATCH_FILE_BYTES, countOf, defaultDelimiter, fileToBase64, matchFileAccept, nameCheckText, noteText, problemTitle, saveBlob } from './matchingCopy';

type Step = 'website' | 'file' | 'review' | 'result';
type Show = 'all' | 'ready' | 'replace' | 'unchanged' | 'problems';

const stepIndex: Record<Step, number> = { website: 0, file: 1, review: 2, result: 3 };
const PAGE = 100;

/**
 * Importing matches from a file, in four steps (CO-170): confirm the website,
 * choose the file, read what it would do row by row, then confirm the rows
 * that were selected. The file has the columns code_ecom (the store SKU),
 * name_ecom, code_erp (the ERP article code) and name_erp, as CSV or Excel.
 * Only the two codes decide anything. The names a file carries are shown as
 * what the person typed; the names Commerce Ops resolved from the store and
 * the ERP are the ones to trust, and they sit beside them.
 *
 * Valid rows are ticked, a row that would replace an existing match is shown
 * with before and after and is never ticked for the person, and invalid rows
 * stay out, with their reasons and a file to download, correct and import
 * again. Nothing is sent before the last confirmation.
 */
export function ImportMatchesDialog({
  open,
  connection,
  linked,
  initialKey,
  withStore,
  onClose,
  onDone,
}: {
  open: boolean;
  connection: ErpConnectionView;
  linked: LinkedWebsite[];
  /** The website the screen is on. */
  initialKey: string;
  withStore: boolean;
  onClose: () => void;
  /** The store was asked something: the list is read again. */
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>('website');
  const [websiteKey, setWebsiteKey] = useState(initialKey);
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<{ title: string; message: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<ErpMatchImportPreview | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set());
  const [show, setShow] = useState<Show>('all');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ErpMatchApplyResult | null>(null);
  const [leftOut, setLeftOut] = useState<LeftOutRow[]>([]);
  const [alreadyMatched, setAlreadyMatched] = useState(0);
  const [dragging, setDragging] = useState(false);
  const idempotency = useIdempotencyKey();
  const fileId = useId();
  const websiteId = useId();
  const input = useRef<HTMLInputElement>(null);

  const target = linked.find((entry) => entry.key === websiteKey) ?? linked[0];
  const website = target ? websiteLabel(target.store, target.website, withStore) : '';

  function reset() {
    setStep('website');
    setWebsiteKey(initialKey);
    setFile(null);
    setProblem(null);
    setPreview(null);
    setTicked(new Set());
    setShow('all');
    setPage(0);
    setResult(null);
    setLeftOut([]);
    setAlreadyMatched(0);
    if (input.current) input.current.value = '';
  }

  function close() {
    onClose();
    // The next opening starts from the first step again.
    window.setTimeout(reset, 200);
  }

  async function check(chosen: File) {
    if (!target) return;
    setProblem(null);
    if (chosen.size > MAX_MATCH_FILE_BYTES) {
      setProblem({ title: 'This file is too large', message: `The file is larger than ${Math.round(MAX_MATCH_FILE_BYTES / 1024)} KB. Split it into smaller files.` });
      return;
    }
    setFile(chosen);
    setChecking(true);
    try {
      const next = await erpApi.previewMatchImport(connection.id, {
        installation_id: target.store.id,
        website_code: target.website.code,
        file_name: chosen.name,
        content_base64: await fileToBase64(chosen),
      });
      setPreview(next);
      // Valid rows are ticked; a replacement is only ever ticked by the person.
      setTicked(new Set(next.rows.filter((row) => row.status === 'ready').map((row) => row.line ?? 0)));
      setShow(next.counts.errors > 0 ? 'problems' : 'all');
      setPage(0);
      setStep('review');
    } catch (cause) {
      const message = cause instanceof ApiError || cause instanceof Error ? cause.message : 'The file could not be checked.';
      setProblem({ title: cause instanceof ApiError && cause.status === 409 ? 'This website cannot save matches' : 'This file cannot be imported', message });
      setStep('file');
    } finally {
      setChecking(false);
      if (input.current) input.current.value = '';
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    if (chosen) void check(chosen);
  }

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragging(false);
    const chosen = event.dataTransfer.files?.[0];
    if (chosen) void check(chosen);
  }

  const rows = useMemo(() => preview?.rows ?? [], [preview]);
  const counts = preview?.counts;
  const shownRows = useMemo(() => {
    switch (show) {
      case 'ready':
        return rows.filter((row) => row.status === 'ready');
      case 'replace':
        return rows.filter((row) => row.status === 'replace');
      case 'unchanged':
        return rows.filter((row) => row.status === 'unchanged');
      case 'problems':
        return rows.filter((row) => row.status === 'error');
      default:
        return rows;
    }
  }, [rows, show]);
  const pageRows = shownRows.slice(page * PAGE, page * PAGE + PAGE);
  const sendable = rows.filter((row) => row.status === 'ready' || row.status === 'replace');
  const tickedRows = sendable.filter((row) => ticked.has(row.line ?? 0));
  const readyRows = rows.filter((row) => row.status === 'ready');
  const allReadyTicked = readyRows.length > 0 && readyRows.every((row) => ticked.has(row.line ?? 0));

  function toggle(line: number, on: boolean) {
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(line);
      else next.delete(line);
      return next;
    });
  }
  function toggleReady(on: boolean) {
    setTicked((current) => {
      const next = new Set(current);
      for (const row of readyRows) {
        if (on) next.add(row.line ?? 0);
        else next.delete(row.line ?? 0);
      }
      return next;
    });
  }

  async function apply() {
    if (!preview || !target || tickedRows.length === 0) return;
    setBusy(true);
    setProblem(null);
    const key = idempotency.key();
    try {
      const done = await erpApi.applyMatches(
        connection.id,
        {
          installation_id: target.store.id,
          website_code: target.website.code,
          source: 'import',
          pairs: tickedRows.map((row) => ({ sku: row.sku, article_code: row.article_code, ...(row.status === 'replace' ? { replace: true } : {}) })),
        },
        key,
      );
      idempotency.settle();
      const out: LeftOutRow[] = [];
      for (const row of rows) {
        if (row.status === 'error') {
          out.push({ sku: row.sku || row.uploaded?.code_ecom || '', articleCode: row.article_code || row.uploaded?.code_erp || '', reason: row.message ?? problemTitle(row.reason), uploaded: row.uploaded });
        } else if ((row.status === 'ready' || row.status === 'replace') && !ticked.has(row.line ?? 0)) {
          out.push({ sku: row.sku, articleCode: row.article_code, reason: row.status === 'replace' ? 'Replacement not chosen' : 'Not ticked' });
        }
      }
      setLeftOut(out);
      setAlreadyMatched(preview.counts.unchanged);
      setResult(done);
      setStep('result');
      onDone();
    } catch (cause) {
      idempotency.settle(cause);
      setProblem({ title: 'The matches could not be sent', message: cause instanceof Error ? cause.message : 'The matches could not be saved.' });
    } finally {
      setBusy(false);
    }
  }

  async function downloadProblems() {
    const bad = rows.filter((row) => row.status === 'error' && row.uploaded);
    try {
      const out = await erpApi.rejectedMatchesFile(connection.id, {
        format: 'xlsx',
        rows: bad.map((row) => ({ ...row.uploaded!, reason: row.message ?? problemTitle(row.reason) })),
      });
      saveBlob(out.blob, out.filename);
    } catch (cause) {
      setProblem({ title: 'The file could not be written', message: cause instanceof Error ? cause.message : 'Try again.' });
    }
  }

  let footer;
  if (step === 'result') {
    footer = (
      <button type="button" className="button" onClick={close}>
        Done
      </button>
    );
  } else if (step === 'review') {
    footer = (
      <>
        <button type="button" className="button button--secondary" onClick={close} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            setStep('file');
            setPreview(null);
          }}
          disabled={busy}
        >
          Choose another file
        </button>
        <button type="button" className="button" onClick={() => void apply()} disabled={busy || tickedRows.length === 0} data-testid="import-save">
          {busy ? 'Asking the store' : `Save ${countOf(tickedRows.length, 'match', 'matches')}`}
        </button>
      </>
    );
  } else if (step === 'file') {
    footer = (
      <>
        <button type="button" className="button button--secondary" onClick={() => setStep('website')} disabled={checking}>
          Back
        </button>
        <button type="button" className="button button--secondary" onClick={close} disabled={checking}>
          Cancel
        </button>
      </>
    );
  } else {
    footer = (
      <>
        <button type="button" className="button button--secondary" onClick={close}>
          Cancel
        </button>
        <button type="button" className="button" onClick={() => setStep('file')} disabled={!target} data-testid="import-continue">
          Continue
        </button>
      </>
    );
  }

  const renderRow = (row: ErpMatchRow) => {
    const uploaded = row.uploaded;
    const replacing = row.status === 'replace';
    const notes = noteText(row.notes);
    return (
      <tr key={row.line} role="row" className={row.status === 'error' ? 'match-plan__row--error' : undefined} data-testid={`import-row-${row.line}`}>
        <td className="match-plan__check" role="cell">
          {row.status === 'ready' || row.status === 'replace' ? (
            <Input
              type="checkbox"
              checked={ticked.has(row.line ?? 0)}
              onChange={(event) => toggle(row.line ?? 0, event.target.checked)}
              aria-label={`${replacing ? 'Replace the match of' : 'Save'} line ${row.line}: ${row.product_name || row.sku} as ${row.article_name || row.article_code}`}
            />
          ) : null}
        </td>
        <td className="tabular-nums" role="cell" data-label="Line">
          {row.line}
        </td>
        <td role="cell" data-label="In your file (names are only for reading)">
          <span className="match-plan__file">
            <code>{uploaded?.code_ecom || '(empty)'}</code> → <code>{uploaded?.code_erp || '(empty)'}</code>
          </span>
          {uploaded?.name_ecom || uploaded?.name_erp ? (
            <span className="match-plan__typed">
              {uploaded?.name_ecom ? `“${uploaded.name_ecom}”` : ''} {uploaded?.name_erp ? `→ “${uploaded.name_erp}”` : ''}
            </span>
          ) : null}
        </td>
        <td role="cell" data-label="Found in Commerce Ops (this decides)">
          {row.product_name || row.article_name ? (
            <>
              <span className="match-plan__primary">{row.product_name || row.sku}</span>
              <code className="match-plan__secondary">{row.sku}</code>
              {row.article_code ? (
                <>
                  <span className="match-plan__arrow" aria-hidden="true">
                    ↓
                  </span>
                  {row.article_name ? (
                    <>
                      <span className="match-plan__primary">{row.article_name}</span>
                      <code className="match-plan__secondary">
                        {row.article_code}
                        {row.article_barcode ? ` · ${row.article_barcode}` : ''}
                      </code>
                    </>
                  ) : (
                    <span className="muted">No article with the code {row.article_code} in the ERP</span>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <span className="muted">Not found</span>
          )}
        </td>
        <td role="cell" data-label="Result">
          <StatusBadge kind="matchRow" value={row.status} label={row.status === 'error' ? problemTitle(row.reason) : undefined} />
          {row.status === 'error' ? <span className="match-plan__detail">{row.message}</span> : null}
          {replacing ? (
            <span className="match-plan__detail match-plan__replace">
              Replaces <code>{row.current_code}</code>
              {row.current_article_name ? ` (${row.current_article_name})` : ''} with <code>{row.article_code}</code>
              {row.article_name ? ` (${row.article_name})` : ''}. Tick it to replace.
            </span>
          ) : null}
          {row.status !== 'error' && row.name_check === 'differ' ? <span className="match-plan__detail match-plan__detail--warn">{nameCheckText.differ}</span> : null}
          {notes.map((text) => (
            <span key={text} className="match-plan__detail match-plan__detail--note">
              {text}
            </span>
          ))}
        </td>
      </tr>
    );
  };

  return (
    <Dialog
      open={open}
      size="lg"
      title="Import matches from a file"
      description="CSV or Excel with the columns code_ecom, name_ecom, code_erp and name_erp."
      onClose={close}
      busy={busy || checking}
      footer={footer}
      testId="import-matches-dialog"
      className="dialog--matches"
    >
      <Stepper
        label="Import steps"
        current={stepIndex[step]}
        steps={[
          { key: 'website', label: 'Website' },
          { key: 'file', label: 'File' },
          { key: 'review', label: 'Review' },
          { key: 'result', label: 'Result' },
        ]}
      />

      {step === 'website' ? (
        <div className="match-import" data-testid="import-step-website">
          <p>
            <code>code_ecom</code> is looked up among this website’s products only.
          </p>
          {linked.length > 1 ? (
            <Field label="Import into">
              <Select id={websiteId} value={websiteKey} onChange={(event) => setWebsiteKey(event.target.value)} data-autofocus>
                {linked.map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {websiteLabel(entry.store, entry.website, withStore)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <p className="match-import__website" data-testid="import-website">
              Importing into <strong>{website}</strong>.
            </p>
          )}
          <p className="field__help">
            The file’s name columns are only for reading. Only <code>code_ecom</code> and <code>code_erp</code> decide which product takes which article, and they are read as text, so leading zeros stay.
          </p>
        </div>
      ) : null}

      {step === 'file' ? (
        <div className="match-import" data-testid="import-step-file">
          <p className="match-import__website">
            Into <strong>{website}</strong>.
          </p>
          <label
            htmlFor={fileId}
            className={`dropzone${dragging ? ' dropzone--over' : ''}${checking ? ' dropzone--busy' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            data-testid="import-dropzone"
          >
            <Icon name="upload" size={20} />
            <span className="dropzone__text">
              <strong>Drop a file here</strong> or choose one
              <span className="dropzone__hint">.csv or .xlsx, up to 5,000 rows</span>
            </span>
            <Input id={fileId} ref={input} className="dropzone__input" type="file" accept={matchFileAccept} onChange={onPick} disabled={checking} data-autofocus />
          </label>
          {checking ? <LoadingRows label={`Checking ${file?.name ?? 'the file'}`} rows={2} rowHeight="32px" /> : null}
          {problem ? (
            <Callout tone="danger" title={problem.title} testId="import-matches-problem">
              {problem.message}
            </Callout>
          ) : null}
          <p className="match-import__example">
            Not sure how it should look?{' '}
            <a className="button button--ghost button--small" href={erpApi.matchExampleUrl(connection.id, { format: 'xlsx' })} download data-testid="import-example-xlsx">
              Download the example (Excel)
            </a>{' '}
            <a className="button button--ghost button--small" href={erpApi.matchExampleUrl(connection.id, { format: 'csv', delimiter: defaultDelimiter() })} download>
              or CSV
            </a>
          </p>
          <ul className="match-import__tips">
            <li>Codes stay as text, so leading zeros stay too. If your program drops them, use the Excel example, or format the code columns as Text.</li>
            <li>An empty ERP code is a mistake in the file, not a request to remove a match.</li>
          </ul>
        </div>
      ) : null}

      {step === 'review' && preview && counts ? (
        <div className="match-import" data-testid="import-matches-review">
          <p className="match-plan__summary">
            <strong>{preview.file.name}</strong> ({preview.file.format === 'xlsx' ? 'Excel' : 'CSV'}) has {countOf(preview.file.rows, 'row')} for <strong>{preview.website.name}</strong>.
            {preview.file.extra_columns.length > 0 ? ` Columns that are not used: ${preview.file.extra_columns.join(', ')}.` : ''}
          </p>
          <dl className="match-plan__counts" aria-label="What the file would do">
            <div>
              <dt>Ready</dt>
              <dd className="tabular-nums" data-testid="import-count-ready">
                {formatNumber(counts.ready)}
              </dd>
            </div>
            <div>
              <dt>Would replace a match</dt>
              <dd className="tabular-nums" data-testid="import-count-replace">
                {formatNumber(counts.replace)}
              </dd>
            </div>
            <div>
              <dt>Already matched</dt>
              <dd className="tabular-nums" data-testid="import-count-unchanged">
                {formatNumber(counts.unchanged)}
              </dd>
            </div>
            <div>
              <dt>Left out: problems</dt>
              <dd className="tabular-nums" data-testid="import-count-errors">
                {formatNumber(counts.errors)}
              </dd>
            </div>
          </dl>
          {counts.errors > 0 ? (
            <p className="match-plan__summary">
              <button type="button" className="button button--ghost button--small" data-testid="import-download-rejected" onClick={() => void downloadProblems()}>
                Download the {countOf(counts.errors, 'row')} with problems
              </button>
            </p>
          ) : null}
          <FilterChips
            label="Show rows"
            value={show}
            onChange={(next) => {
              setShow(next);
              setPage(0);
            }}
            options={[
              { value: 'all' as Show, label: 'All rows', count: counts.total },
              { value: 'ready' as Show, label: 'Ready', count: counts.ready },
              { value: 'replace' as Show, label: 'Replace', count: counts.replace },
              { value: 'unchanged' as Show, label: 'Already matched', count: counts.unchanged },
              { value: 'problems' as Show, label: 'Problems', count: counts.errors },
            ]}
          />
          {problem ? (
            <Callout tone="danger" title={problem.title} testId="import-matches-problem">
              {problem.message}
            </Callout>
          ) : null}
          {shownRows.length === 0 ? (
            <p className="quiet-empty" data-testid="import-matches-none">
              {show === 'problems' ? 'No problems' : 'No rows'}
            </p>
          ) : (
            <Table label="Rows of the file and what each would do" compact keepRoles className="match-plan match-plan--import" wrapperClassName="match-plan__scroll">
              <caption className="visually-hidden">Rows of the file and what each would do</caption>
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" className="match-plan__check" role="columnheader">
                    <Input
                      type="checkbox"
                      checked={allReadyTicked}
                      disabled={readyRows.length === 0}
                      onChange={(event) => toggleReady(event.target.checked)}
                      aria-label="Tick every ready row"
                    />
                  </th>
                  <th scope="col" role="columnheader">
                    Line
                  </th>
                  <th scope="col" role="columnheader">
                    In your file (names are only for reading)
                  </th>
                  <th scope="col" role="columnheader">
                    Found in Commerce Ops (this decides)
                  </th>
                  <th scope="col" role="columnheader">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody role="rowgroup">{pageRows.map(renderRow)}</tbody>
            </Table>
          )}
          {shownRows.length > PAGE ? (
            <Pager
              first={page * PAGE + 1}
              last={Math.min(page * PAGE + PAGE, shownRows.length)}
              total={shownRows.length}
              onPrevious={() => setPage(page - 1)}
              onNext={() => setPage(page + 1)}
            />
          ) : null}
        </div>
      ) : null}

      {step === 'result' && result ? (
        <BatchResult connectionId={connection.id} result={result} leftOut={leftOut} alreadyMatched={alreadyMatched} rejectedDownload onChanged={onDone} />
      ) : null}
    </Dialog>
  );
}
